import type { RouteConfig } from '../types';
import * as Components from '../lazyComponents';
import { Navigate } from 'react-router-dom';
import { ErpDisponible } from '@/features/erp/components/ErpDisponible';

/**
 * ERP routes — Sprint 8 (IRIS lane).
 *
 * Decision arquitectonica corregida 2026-05-25 (TINA + Randhy):
 *   - Path raiz nuevo `/erp/*` (NO `/admin/erp` ni `/agencia/erp`).
 *   - Construir limpio en `features/erp/`, NO reusar `features/agency/erp`
 *     ni `features/accounting/` (vieja deuda heredada del scaffolding mock).
 *   - Roles: `AGENCY_ADMIN` (Paul), `AGENCY_ACCOUNTING` (subrol contadora),
 *     `SUPER_ADMIN` (tenant impersonation — NO cross-tenant).
 *
 * Roadmap (segun issues del Sprint 8):
 *   /erp/dashboard                         → ERP-8.1 Journey Paul ✅
 *   /erp/journal-entries                   → ERP-8.2 Contadora (lista)
 *   /erp/journal-entries/nuevo             → ERP-8.2 Contadora (form partida doble)
 *   /erp/journal-entries/:id               → ERP-8.2 Contadora (detail)
 *   /erp/reports/{606,607,608}/:y/:m       → ERP-8.2 DGII reports
 *   /erp/dgii/ecf                          → ERP-8.3 Contadora dia normal (futuro)
 *   /erp/dgii/p12 + /erp/dgii/ncf          → ERP-8.4 Contadora trimestral (futuro)
 *   /erp/comisiones                        → ERP-8.6 Journey Agente (futuro)
 *
 * Mantener separacion de concerns: NO extender accountingRoutes ni agencyRoutes.
 */
// BLOCKER 1 PR #26 (TINA review 2026-05-25 + ATHENA/Gabriel AG10-09):
// `AGENCY_ACCOUNTING` no existe en `apps/api/src/core/security/enums/user-role.enum.ts`.
// El rol que cubre "contadora interna de agencia" es `ACCOUNTING_TEAM` (full ERP access).
const ERP_ROLES = ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'] as const;

// AGENCY_AGENT_USER se agrega solo para rutas especificas del agente (mis
// comisiones). NO entra a /erp/dashboard ni a las demas vistas ejecutivas.
const ERP_ROLES_WITH_AGENT = [
  'AGENCY_ADMIN',
  'ACCOUNTING_TEAM',
  'SUPER_ADMIN',
  'AGENCY_AGENT_USER',
] as const;

const erpRoutesBase: RouteConfig[] = [
  // /erp -> /erp/dashboard (entry redirect)
  {
    path: '/erp',
    element: <Navigate to="/erp/dashboard" replace />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: false,
  },
  // ERP-8.1 — Journey Paul
  {
    path: '/erp/dashboard',
    element: <Components.ErpExecutiveDashboardPage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },

  // ERP-8.2 — Journey Contadora cierre mensual

  // Lista de asientos (vista 4)
  {
    path: '/erp/journal-entries',
    element: <Components.JournalEntriesListPage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  // Nuevo asiento (vista 6 — CRITICO partida doble)
  // Importante: declarar ANTES de /:id para que React Router no la trate
  // como `id` con valor "nuevo".
  {
    path: '/erp/journal-entries/nuevo',
    element: <Components.JournalEntryNewPage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  // Detalle de asiento (vista 5)
  {
    path: '/erp/journal-entries/:id',
    element: <Components.JournalEntryDetailPage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },

  // DGII reports — comparten DgiiReportTemplate, diferencia el code (vistas 7-9)
  {
    path: '/erp/reports/606/:year/:month',
    element: <Components.DgiiReport606Page />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  {
    path: '/erp/reports/606',
    element: <Components.DgiiReport606Page />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  {
    path: '/erp/reports/607/:year/:month',
    element: <Components.DgiiReport607Page />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  {
    path: '/erp/reports/607',
    element: <Components.DgiiReport607Page />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  {
    path: '/erp/reports/608/:year/:month',
    element: <Components.DgiiReport608Page />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  {
    path: '/erp/reports/608',
    element: <Components.DgiiReport608Page />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },

  // ERP-8.3 — Journey Contadora dia normal (Cola e-CF + Detail)
  {
    path: '/erp/dgii/ecf',
    element: <Components.EcfQueueListPage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  {
    path: '/erp/dgii/ecf/:id',
    element: <Components.EcfDetailPage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },

  // ERP-8.4 — Journey Contadora trimestral (P12 + NCF + Plan Contable)
  {
    path: '/erp/dgii/p12',
    element: <Components.P12CertificatesPage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  {
    path: '/erp/dgii/ncf',
    element: <Components.NcfRangesPage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },
  {
    path: '/erp/cuentas',
    element: <Components.AccountsTreePage />,
    roles: [...ERP_ROLES],
    requiresAuth: true,
    requiresLayout: true,
  },

  // ERP-8.6 — Journey Agente (Mis comisiones)
  // Unica ruta abierta a AGENCY_AGENT_USER. El agente solo ve sus propias
  // comisiones (filtro JWT-based en backend).
  {
    path: '/erp/comisiones',
    element: <Components.AgenteCommissionsPage />,
    roles: [...ERP_ROLES_WITH_AGENT],
    requiresAuth: true,
    requiresLayout: true,
  },
];

/**
 * GH#2765 — el ERP contable NO está desplegado en producción (INBO_ERP_API_URL ausente: las rutas /erp/* dan 503 ERP_NOT_AVAILABLE). Decisión
 * de Randhy (13-oct, vía la HUB): el ERP NO se quita del menú, «es mejor solo terminarlo». Cada pantalla va envuelta en `ErpDisponible`: si el
 * API dice que el ERP no existe, muestra «en construcción» (sin error ni KPIs en cero). El menú no cambia.
 */
export const erpRoutes: RouteConfig[] = erpRoutesBase.map((ruta) => ({
  ...ruta,
  element: <ErpDisponible>{ruta.element}</ErpDisponible>,
}));
